import React, { useEffect, useMemo } from "react";
import { Box, Chip, Divider, List, ListItem, ListItemText, Paper, Stack, Typography } from "@mui/material";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useUserData } from "../../context/UserDataFetcher";
import { useHistory } from "react-router";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 4,
      borderRadius: 3,
      borderColor: "divider",
      backgroundColor: "background.paper",
    }}
  >
    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    <Divider sx={{ mb: 3 }} />
    <Stack spacing={2}>{children}</Stack>
  </Paper>
);

const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
  <Box
    component="pre"
    sx={{
      fontFamily: "Fira Code, Menlo, Consolas, 'Courier New', monospace",
      fontSize: "0.95rem",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      backgroundColor: "background.default",
      borderRadius: 2,
      border: (theme) => `1px solid ${theme.palette.divider}`,
      p: 2.5,
      m: 0,
    }}
  >
    {children}
  </Box>
);

const ManufacturerApiDocs: React.FC = () => {
  const userData = useUserData();
  const history = useHistory();

  const isManufacturerAdmin =
    userData?.role === "manufacturer" &&
    (userData?.isAdmin || userData?.isManufacturerPrincipal);

  useEffect(() => {
    if (userData && !isManufacturerAdmin) {
      history.replace("/admin/home");
    }
  }, [userData, isManufacturerAdmin, history]);

  if (!userData || !isManufacturerAdmin) {
    return null;
  }

  const manufacturerId = userData?.manufacturerId ?? "acme-mfg";
  const manufacturerPrefix = useMemo(() => {
    const normalized = manufacturerId.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (!normalized) return "MF-";
    return `${normalized.slice(0, 4)}-`;
  }, [manufacturerId]);
  const sampleOrderNumber = "102934";
  const sampleShipmentNumber = "SHIP-1001";
  const sampleOrderId = `${manufacturerPrefix}${sampleOrderNumber}`;
  const sampleShipmentId = `${manufacturerPrefix}${sampleShipmentNumber}`;
  const sampleOrderBarcode = `${sampleOrderNumber}-001`;
  const sampleRemoveItemId = `${sampleOrderId}-old-sample`;

  return (
    <DashboardLayout>
      <Stack spacing={4}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            background: (theme) =>
              `linear-gradient(140deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 60%)`,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              Manufacturer API Documentation
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Use these REST endpoints to create, update, and remove dealer orders and their
              items in Cabinet Logger. Each call requires your manufacturer-issued API key,
              an HMAC-SHA256 signature, and writes directly to the live order collections that
              power dealer dashboards.
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Chip
                color="primary"
                label="Audience: Manufacturer Admins & Principals"
                sx={{ fontWeight: 600 }}
              />
              <Chip color="secondary" label="Auth: HMAC signed HTTPS" />
              <Chip color="default" label="Scope: Orders & Items" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Examples on this page assume <code>{manufacturerId}</code> as the manufacturerId and{" "}
              <code>{manufacturerPrefix}</code> as the prefix.
            </Typography>
          </Stack>
        </Paper>

        <Section title="Who Can Use These APIs">
          <Typography>
            Only manufacturer administrators and manufacturer principals may access these
            endpoints. Requests are scoped to the manufacturer (tenant) encoded in the API
            key, and cross-tenant writes are rejected with <code>tenant_mismatch</code>.
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText
                primary="Roles"
                secondary="Cabinet Logger checks that your user record is flagged as an admin or principal for the manufacturer before surfacing this documentation."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="Data separation"
                secondary="Each call must include your manufacturerId and it must match the tenant associated with your API key."
              />
            </ListItem>
          </List>
        </Section>

        <Section title="Authentication & Request Signing">
          <Typography>
            Every request must include an API key and an HMAC-SHA256 signature generated
            with the shared secret stored in Firestore under <code>apiKeys/&lt;key&gt;</code>.
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText
                primary={<Typography fontWeight={600}>Headers (required)</Typography>}
                secondary={
                  <>
                    <Box component="div">
                      <code>X-Api-Key</code> – your issued API key.
                    </Box>
                    <Box component="div">
                      <code>X-Signature</code> – the literal prefix <code>sha256=</code>{" "}
                      followed by the lowercase hex digest of the HMAC over the raw JSON request
                      body.
                    </Box>
                  </>
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary={<Typography fontWeight={600}>Signing process</Typography>}
                secondary={
                  <>
                    <Box component="div">1. Serialize your payload with <code>JSON.stringify</code>.</Box>
                    <Box component="div">2. Compute <code>digest = HMAC_SHA256(body, secret)</code>.</Box>
                    <Box component="div">3. Send <code>X-Signature: sha256={"{digest}"}</code>.</Box>
                  </>
                }
              />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary">
            The request body must be identical to the bytes used when computing the HMAC.
            Avoid middleware that mutates JSON payloads after signing.
          </Typography>
          <CodeBlock>{`import crypto from "crypto";

const body = JSON.stringify(payload);
const signature = crypto
  .createHmac("sha256", API_SECRET)
  .update(body)
  .digest("hex");

const headers = {
  "Content-Type": "application/json",
  "X-Api-Key": API_KEY,
  "X-Signature": \`sha256=\${signature}\`,
};`}</CodeBlock>
        </Section>

        <Section title="Endpoint Hostnames">
          <Typography>
            The functions are deployed in Firebase under your Cabinet Logger project. Combine
            the Cloud Functions base URL with the endpoint names below.
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 2, flex: 1 }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Production
              </Typography>
              <Typography variant="body2">
                <code>https://us-central1-&lt;project-id&gt;.cloudfunctions.net</code>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Replace <code>&lt;project-id&gt;</code> with the Firebase project configured in
                <code>firebaseConfig.ts</code>.
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 2, flex: 1 }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Testing locally
              </Typography>
              <Typography variant="body2">
                Export <code>API_URL</code>, <code>API_KEY</code>, and <code>API_SECRET</code>{" "}
                (optionally <code>ORDER_ID</code>), then run{" "}
                <code>node tools/sign-and-post.mjs</code>. The helper builds the signed payload
                and posts to <code>/createOrder</code>, echoing the HTTP status and response.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                For Firebase emulators, point <code>API_URL</code> to{" "}
                <code>http://127.0.0.1:5001/&lt;project-id&gt;/us-central1</code> with the
                Functions emulator running; otherwise use the production base URL above for
                staging validations.
              </Typography>
            </Paper>
          </Stack>
          <CodeBlock>{`curl -X POST "https://us-central1-<project-id>.cloudfunctions.net/createOrder" \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: $API_KEY" \\
  -H "X-Signature: sha256=$SIGNATURE" \\
  -d @payload.json`}</CodeBlock>
        </Section>

        <Section title="POST /createOrder">
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label="POST" color="primary" sx={{ fontWeight: 600 }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              /createOrder
            </Typography>
          </Stack>
          <Typography>
            Creates a new order document under <code>mainorders/{`{orderId}`}</code> and, if
            items are supplied, upserts each item under <code>mainitems/{`{itemId}`}</code>.
            The order ID is automatically derived from your manufacturer prefix plus the
            provided <code>orderNumber</code>.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Request body
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText
                primary="manufacturerId (string, required)"
                secondary="Must match the tenant encoded in your API key."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="dealerId (string, required)"
                secondary="Order ownership for downstream dashboards. If omitted, provide dealerNumber and Cabinet Logger resolves it automatically."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="dealerNumber (string, optional)"
                secondary="Manufacturer-specific dealer number. When present, the API looks up the dealerId by matching manufacturerDealerNumbers[manufacturerId] in the dealers collection."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="orderNumber (string, required)"
                secondary="Preserved verbatim; combined with your manufacturer prefix to build the document ID."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="status (string, optional)"
                secondary="Defaults to pending."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="shipmentIds (string[], optional)"
                secondary="Stored in the order for quick lookups."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="itemIds (string[], optional)"
                secondary="Used when you need to reference existing items without re-sending them."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="items (object[], optional)"
                secondary="Full item definitions. IDs are automatically normalized to start with prefix + orderNumber and cabinet & barcode values are derived when omitted. If you omit id but provide barcodeId, Cabinet Logger derives the item id by prepending your manufacturer prefix to that barcode."
              />
            </ListItem>
          </List>
          <CodeBlock>{`{
  "manufacturerId": "${manufacturerId}",
  "prefix": "${manufacturerPrefix}",
  "dealerId": "BestCabinets",
  "dealerNumber": "1234567",
  "orderNumber": "102934",
  "status": "created",
  "items": [
    {
      "barcodeId": "${sampleOrderBarcode}",
      "description": "Island Base Cabinet",
      "cabinetNumber": "001",
      "destination": "123 Main St, Springfield, IL",
      "shipmentIds": ["ship-7788"]
    }
  ]
}`}</CodeBlock>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Response
          </Typography>
          <CodeBlock>{`HTTP 200
{
  "ok": true,
  "id": "${sampleOrderId}"
}`}</CodeBlock>
          <Typography variant="body2" color="text.secondary">
            The <code>id</code> field is the canonical order document ID (manufacturer prefix +
            orderNumber) persisted in <code>mainorders</code>. Non-200 responses return the structured
            error payload documented below, including <code>error</code>, <code>message</code>,
            <code>retryable</code>, and <code>requestId</code>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If the manufacturer document does not have a configured <code>prefix</code>, the
            function falls back to <code>FAIL-</code> and flags the order with
            <code>prefixMissing = true</code>.
          </Typography>
        </Section>

        <Section title="PATCH /updateOrder">
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label="PATCH" color="info" sx={{ fontWeight: 600 }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              /updateOrder
            </Typography>
          </Stack>
          <Typography>
            Applies partial updates to an existing order and item set. Fields that are omitted
            are left unchanged. Item payloads are upserted (created or overwritten) and you can
            additionally supply <code>removeItemIds</code> to delete item documents.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Request body
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText
                primary="id (string, optional)"
                secondary="Canonical order document ID (prefix + orderNumber). When omitted, include orderNumber (and optionally prefix) so Cabinet Logger can derive it automatically."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="prefix (string, optional)"
                secondary="Only needed when you want to override the manufacturer's stored prefix while deriving order and item IDs."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="manufacturerId (string, required)"
                secondary="Must match your tenant."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="dealerId, status, orderNumber (optional)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="dealerNumber (string, optional)"
                secondary="Fallback identifier if dealerId is unknown; Cabinet Logger resolves it to the canonical dealerId for the manufacturer."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="shipmentIds, itemIds (optional arrays)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="items (optional array)"
                secondary="Same shape as the create endpoint. When provided, each item is upserted and barcodes are regenerated if the cabinet number changes. Omitting id is allowed when barcodeId is supplied; the system will prefix your barcode to form the item document id."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="removeItemIds (optional array)"
                secondary="Each matching item document is deleted after the upsert phase."
              />
            </ListItem>
          </List>
          <CodeBlock>{`{
  "id": "${sampleOrderId}",
  "manufacturerId": "${manufacturerId}",
  "prefix": "${manufacturerPrefix}",
  "dealerNumber": "1234567",
  "status": "inTransit",
  "items": [
    {
      "cabinetNumber": "ZONE-A1",
      "barcodeId": "${sampleOrderBarcode}",
      "shipmentIds": ["ship-9900"]
    }
  ],
  "removeItemIds": ["${sampleRemoveItemId}"]
}`}</CodeBlock>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Response
          </Typography>
          <CodeBlock>{`HTTP 200
{
  "ok": true,
  "id": "${sampleOrderId}"
}`}</CodeBlock>
          <Typography variant="body2" color="text.secondary">
            The response echoes the stabilized order ID. Items listed in <code>items</code> are
            upserted atomically before the <code>removeItemIds</code> deletions are applied.
            Partial updates that do not modify items still return the same structure.
          </Typography>
        </Section>

        <Section title="DELETE /deleteOrder">
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label="DELETE" color="error" sx={{ fontWeight: 600 }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              /deleteOrder
            </Typography>
          </Stack>
          <Typography>
            Deletes the specified order and every item document linked by <code>orderId</code>.
            The request can include parameters in the JSON body or as query string values.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Parameters
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText
                primary="id (string, optional)"
                secondary="Order document ID to remove. If omitted, provide orderNumber and optionally prefix."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="manufacturerId (string, required)"
                secondary="Must match the API key tenant."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="orderNumber (string, optional)"
                secondary="Used with prefix (or the manufacturer's stored prefix) to derive the orderId when you don't pass id."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="prefix (string, optional)"
                secondary="Override for the manufacturer prefix when deriving IDs. If omitted, the server looks up the prefix on the manufacturer document."
              />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            If you skip <code>id</code>, supply <code>orderNumber</code> and optionally
            <code>prefix</code>; otherwise the server pulls the prefix from your manufacturer
            profile before constructing the canonical ID.
          </Typography>
          <CodeBlock>{`curl -X DELETE "https://us-central1-<project-id>.cloudfunctions.net/deleteOrder" \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: $API_KEY" \\
  -H "X-Signature: sha256=$SIGNATURE" \\
  -d '{"id":"${sampleOrderId}","manufacturerId":"${manufacturerId}","prefix":"${manufacturerPrefix}"}'`}</CodeBlock>
          <Typography variant="body2" color="text.secondary">
            Before deleting the order, the function loads all <code>mainitems</code> documents
            referencing the order and removes them in batches of 500.
          </Typography>
        </Section>

        <Section title="POST /createShipment">
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label="POST" color="primary" sx={{ fontWeight: 600 }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              /createShipment
            </Typography>
          </Stack>
          <Typography>
            Creates a shipment document under <code>mainshipments/{`{prefix+id}`}</code>. Provide the
            human-friendly shipment number in <code>id</code>; the API prefixes the Firestore
            document ID automatically and stores the un-prefixed value in the payload.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Request body
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText primary="manufacturerId (string, required)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="prefix (string, optional)"
                secondary="Override the manufacturer prefix if you need to force a particular code."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="id (string, required)"
                secondary="Un-prefixed shipment number; the API handles prefixing the document ID."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="dealerIds (string[], optional)"
                secondary="Canonical dealer doc IDs. Use dealerNumber when you only have the manufacturer-specific number."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="dealerNumber (string, optional)"
                secondary="When present, Cabinet Logger resolves the canonical dealerId using manufacturerDealerNumbers."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="orderIds (string[], optional)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="ordersSnapshot (object, optional)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="shipmentDate, status, truckId, drivers, destinations (optional)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="delayReason, processed, processedAt, issueSummary (optional)" />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary">
            Fields such as <code>delayReason</code>, <code>processed</code>, and
            <code>issueSummary</code> are optional telemetry helpers you can omit unless your ERP
            tracks those values.
          </Typography>
          <CodeBlock>{`{
  "manufacturerId": "${manufacturerId}",
  "prefix": "${manufacturerPrefix}",
  "id": "${sampleShipmentNumber}",
  "dealerIds": ["BestCabinets"],
  "dealerNumber": "1234567",
  "orderIds": ["${sampleOrderId}"],
  "shipmentDate": "2025-11-01T12:00:00.000Z",
  "status": "created",
  "drivers": ["Pat Driver"],
  "destinations": ["456 Oak Ave, Springfield, IL"]
}`}</CodeBlock>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Response
          </Typography>
          <CodeBlock>{`HTTP 200
{
  "ok": true,
  "id": "${sampleShipmentId}",
  "shipmentNumber": "${sampleShipmentNumber}"
}`}</CodeBlock>
          <Typography variant="body2" color="text.secondary">
            The <code>id</code> in the response is the canonical Firestore ID (prefix + shipment
            number); <code>shipmentNumber</code> echoes the un-prefixed value.
          </Typography>
        </Section>

        <Section title="PATCH /updateShipment">
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label="PATCH" color="info" sx={{ fontWeight: 600 }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              /updateShipment
            </Typography>
          </Stack>
          <Typography>
            Updates shipment metadata (status, truck, logistics notes). Order membership continues to
            flow through the order APIs, so <code>orderIds</code>, <code>dealerIds</code>, and
            <code>ordersSnapshot</code> are read-only on this endpoint.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Request body
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText primary="manufacturerId (string, required)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="id / prefixedId (string, optional)"
                secondary="Provide either the un-prefixed shipment number (id) or the canonical Firestore ID (prefixedId)."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="prefix (string, optional)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="shipmentDate, status, truckId, drivers, destinations, destination"
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="destinationsGeo, delayReason, processed, processedAt, issueSummary"
              />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary">
            Sending <code>orderIds</code>, <code>dealerIds</code>, or <code>ordersSnapshot</code>
            results in <code>read_only_* </code> validation errors since those fields are managed by
            the order workflow.
          </Typography>
          <CodeBlock>{`{
  "manufacturerId": "${manufacturerId}",
  "prefix": "${manufacturerPrefix}",
  "id": "${sampleShipmentNumber}",
  "status": "inTransit",
  "truckId": "truck-12",
  "drivers": ["Pat Driver", "Chris CoDriver"],
  "delayReason": "Weather delay"
}`}</CodeBlock>
        </Section>

        <Section title="DELETE /deleteShipment">
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label="DELETE" color="error" sx={{ fontWeight: 600 }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              /deleteShipment
            </Typography>
          </Stack>
          <Typography>
            Removes a shipment document identified by the un-prefixed <code>id</code> or canonical
            <code>prefixedId</code>. Orders/items continue to exist, so use the order APIs if you
            also need to remove linked inventory.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Parameters
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText primary="manufacturerId (string, required)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="id (string, optional)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="prefixedId (string, optional)" />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="prefix (string, optional)" />
            </ListItem>
          </List>
          <CodeBlock>{`curl -X DELETE "https://us-central1-<project-id>.cloudfunctions.net/deleteShipment" \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: $API_KEY" \\
  -H "X-Signature: sha256=$SIGNATURE" \\
  -d '{"manufacturerId":"${manufacturerId}","id":"${sampleShipmentNumber}","prefix":"${manufacturerPrefix}"}'`}</CodeBlock>
        </Section>

        <Section title="Validation & Error Codes">
          <Typography>
            Every non-200 response follows the structure{" "}
            <code>{"{ \"error\": \"code\", \"message\": \"...\", \"retryable\": false, \"requestId\": \"trace\" }"}</code>.
            Use <code>retryable</code> to determine whether the caller should automatically retry,
            and <code>requestId</code> to correlate with Cloud Logging.
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText
                primary="400 invalid_*"
                secondary="Field-level validation failure. The suffix indicates which property was rejected (e.g., invalid_order_number, invalid_item_id)."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="401 unauthorized / bad_signature"
                secondary="Missing API key header or the HMAC signature did not match the raw request body."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="403 tenant_mismatch"
                secondary="manufacturerId in the payload differs from the tenant associated with your API key."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="405 method_not_allowed"
                secondary="HTTP method does not match the expected verb."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="409 order_id_exists"
                secondary="Attempted to create a duplicate order ID. Use PATCH /updateOrder instead of POST /createOrder."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="409 shipment_id_exists"
                secondary="Attempted to create a duplicate shipment ID. Use PATCH /updateShipment instead."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="400 invalid_dealer_number"
                secondary="dealerNumber did not match any dealer under the specified manufacturer."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="400 read_only_*"
                secondary="Raised when PATCH /updateShipment receives orderIds, dealerIds, or ordersSnapshot fields, which are managed by the order APIs."
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="500 internal"
                secondary="Unexpected failure; review Cloud Logging for stack traces and retry once transient issues are resolved."
              />
            </ListItem>
          </List>
        </Section>

        <Section title="Local Utilities & Further Reading">
          <Typography>
            The repository includes tooling and shared schemas that pair with these endpoints:
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText
                primary="Signature helper"
                secondary={
                  <>
                    Run <code>node tools/sign-and-post.mjs</code> to send signed payloads while
                    developing integrations.
                  </>
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="Functions source"
                secondary={
                  <>
                    Schema normalization, barcode generation, and cleanup rules mirror the
                    dealer dashboards. Your implementation specialist can provide the latest
                    schema reference or sample payloads on request.
                  </>
                }
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary="Import pipeline"
                secondary={
                  <>
                    Bulk ingestion workflows live in <code>functions/src/import/imports.ts</code>{" "}
                    and use Firebase auth tokens instead of HMAC keys.
                  </>
                }
              />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary">
            Need an API key rotated or a new tenant created? Contact the Cabinet Logger support
            team or your implementation specialist. Keys are stored securely in Firestore and
            can be revoked instantly.
          </Typography>
        </Section>
      </Stack>
    </DashboardLayout>
  );
};

export default ManufacturerApiDocs;
